import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Check, Shield, ShieldCheck, BadgeCheck, Lock, Tag, TrendingUp, Globe, CheckCircle, Server, HelpCircle, Mail, MessageCircle, Clock, Award, Star, Crown, ArrowRight, BookOpen, X as XIcon } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { API_URLS } from '../../config/api';
import MediaDisplay from '../MediaDisplay';

const fallbackTabs = [
  { key: 'all', label: 'Tümü' },
  { key: 'dv', label: 'Hızlı Kurulum (DV SSL)' },
  { key: 'ov', label: 'Kurumsal (OV SSL)' },
  { key: 'ev', label: 'Gelişmiş Kurumsal (EV SSL)' },
  { key: 'wildcard', label: 'Subdomain (Wilcard SSL)' },
  { key: 'deals', label: 'Fırsat Paketleri' }
];

// Temporary seed data adapted from ssl-sertifikasi.html structure
const seedCertificates = [
  {
    id: 'dv-rapidssl',
    name: 'RapidSSL DV',
    type: 'dv',
    warranty: '$10,000',
    features: [
      { name: 'Dakikalar içinde kurulum', text: 'Dakikalar içinde kurulum', included: true },
      { name: 'Domain Doğrulama (DV)', text: 'Domain Doğrulama (DV)', included: true },
      { name: '99% tarayıcı uyumluluğu', text: '99% tarayıcı uyumluluğu', included: true }
    ],
    price: '15,99$ /yıllık',
    period: '',
    best: true
  },
  {
    id: 'ov-geotrust',
    name: 'GeoTrust True BusinessID OV',
    type: 'ov',
    warranty: '$250,000',
    features: [
      { name: 'Kurumsal Doğrulama (OV)', text: 'Kurumsal Doğrulama (OV)', included: true },
      { name: 'Kurumsal güven damgası', text: 'Kurumsal güven damgası', included: true },
      { name: 'Gelişmiş güven', text: 'Gelişmiş güven', included: true }
    ],
    price: '89,99$ /yıllık',
    period: '',
    best: false
  },
  {
    id: 'ev-digicert',
    name: 'DigiCert EV',
    type: 'ev',
    warranty: '$1,000,000',
    features: [
      { name: 'Gelişmiş Kurumsal Doğrulama (EV)', text: 'Gelişmiş Kurumsal Doğrulama (EV)', included: true },
      { name: 'Adres çubuğunda şirket adı', text: 'Adres çubuğunda şirket adı', included: true },
      { name: 'En yüksek güven', text: 'En yüksek güven', included: true }
    ],
    price: '199,99$ /yıllık',
    period: '',
    best: false
  },
  {
    id: 'wildcard-positive',
    name: 'PositiveSSL Wildcard',
    type: 'wildcard',
    warranty: '$10,000',
    features: [
      { name: 'Sınırsız alt alan adı', text: 'Sınırsız alt alan adı', included: true },
      { name: 'Domain Doğrulama (DV)', text: 'Domain Doğrulama (DV)', included: true },
      { name: 'Hızlı kurulum', text: 'Hızlı kurulum', included: true }
    ],
    price: '99,99$ /yıllık',
    period: '',
    best: false
  },
  {
    id: 'deal-rapidssl',
    name: 'RapidSSL Promo',
    type: 'deals',
    warranty: '$10,000',
    features: [
      { name: 'Kampanyalı fiyat', text: 'Kampanyalı fiyat', included: true },
      { name: 'DV anında kurulum', text: 'DV anında kurulum', included: true },
      { name: 'SEO dostu', text: 'SEO dostu', included: true }
    ],
    price: '12,99$ /yıllık',
    period: '',
    best: true
  }
];

const TypeIcon = ({ type }) => {
  switch (type) {
    case 'dv':
      return <BadgeCheck className="w-5 h-5 text-yellow-600" />;
    case 'ov':
      return <ShieldCheck className="w-5 h-5 text-yellow-600" />;
    case 'ev':
      return <Lock className="w-5 h-5 text-yellow-600" />;
    case 'wildcard':
      return <Shield className="w-5 h-5 text-yellow-600" />;
    case 'deals':
      return <Tag className="w-5 h-5 text-yellow-600" />;
    default:
      return <Shield className="w-5 h-5 text-yellow-600" />;
  }
};

const CertificateCard = ({ cert, index }) => {
  // Özellikleri normalize et - obje yapısını koru
  const normalizedFeatures = (cert.features || []).map(f => {
    if (typeof f === 'string') {
      return { name: f, text: f, included: true };
    }
    if (f && typeof f === 'object') {
      return {
        name: f.name || f.text || '',
        text: f.text || f.name || '',
        included: f.included !== false
      };
    }
    return { name: String(f), text: String(f), included: true };
  });

  return (
    <Card
      className={`relative border-0 shadow-xl cursor-pointer ${
        cert.best ? 'ring-4 ring-purple-200' : ''
      } bg-white`}
    >
      {/* Popular Badge */}
      {cert.best && (
        <div className="absolute top-0 transform -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: '52%' }}>
          <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold" style={{ width: '150px' }}>
            <Crown className="w-4 h-4 mr-1" />
            EN POPÜLER
          </Badge>
        </div>
      )}

      <CardHeader className="space-y-4 pb-6">
        <div className="space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-600 to-amber-700 flex items-center justify-center">
            <Shield className="w-8 h-8 text-white" />
          </div>
          
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold text-gray-900">
              {cert.name}
            </CardTitle>
            <p className="text-gray-600 leading-relaxed">Garanti: {cert.warranty}</p>
          </div>
        </div>

        {/* Pricing */}
        <div className="space-y-2">
          <div className="flex items-baseline space-x-2">
            <span className="text-4xl font-bold text-gray-900">
              {cert.price}
            </span>
            <span className="text-gray-600">
              {cert.period || '/yıllık'}
            </span>
          </div>

          {/* Yıllık İndirim */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              SSL sertifikanızla güvenli iletişim <span className="font-bold text-yellow-900">SAĞLAYıN</span>
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Features */}
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-800">Özellikler:</h4>
          {normalizedFeatures.map((feature, idx) => {
            const isIncluded = feature.included !== false;
            // Güvenli string extraction
            const featureText = typeof feature === 'string'
              ? feature
              : (feature?.text || feature?.name || String(feature));
            return (
              <div 
                key={idx} 
                className="flex items-center space-x-3"
              >
                {isIncluded ? (
                  <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                ) : (
                  <XIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
                <span className={`text-sm font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                  {featureText}
                </span>
              </div>
            );
          })}
        </div>

        {/* CTA Button */}
        <Button 
          className="w-full h-12 font-semibold bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-700 hover:to-amber-700 text-white"
        >
          <span>PLANI SEÇ</span>
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

const SSLCertificatesPage = () => {
  const [active, setActive] = useState('all');
  const [tabs, setTabs] = useState(fallbackTabs);
  const [certs, setCerts] = useState(seedCertificates);
  const [blogs, setBlogs] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [whatsappNumber, setWhatsappNumber] = useState('905324404011');
  const [contentReady, setContentReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Initialize from query (?tab=dv|ov|ev|wildcard|deals|all) or hash (#evSsl, #dvSsl, etc.)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qp = (params.get('tab') || '').toLowerCase();
    const valid = ['all', 'dv', 'ov', 'ev', 'wildcard', 'deals'];
    if (valid.includes(qp)) {
      setActive(qp);
      return;
    }
    // Check hash (e.g., #evSsl, #evssl, #ev)
    const hash = (location.hash || window.location.hash || '').toLowerCase().replace('#', '');
    if (hash.includes('hizlikurulum') || hash.includes('dv') || hash === 'dvssl') {
      setActive('dv');
    } else if (hash.includes('premium') || hash.includes('ov') || hash === 'ovssl') {
      setActive('ov');
    } else if (hash.includes('ev') || hash === 'evssl') {
      setActive('ev');
    } else if (hash.includes('wildcard') || hash === 'wildcardssl') {
      setActive('wildcard');
    } else if (hash.includes('deal') || hash.includes('firsat')) {
      setActive('deals');
    }
  }, [location.search, location.hash]);

  // Fetch page content (DB)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getPageContent&slug=ssl-sertifikasi`);
        const txt = await res.text();
        let json; try { json = JSON.parse(txt); } catch { json = {}; }
        if (json?.status === 'success' && json?.data) {
          setPageContent(json.data);
        }
      } catch (e) {
        // ignore, fallback content remains
      }
    })();
  }, []);

  // Fetch tabs, packages, blogs, and FAQs
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [tRes, pRes, bRes, fRes] = await Promise.all([
          fetch(API_URLS.SSL_TABS),
          fetch(API_URLS.SSL_PACKAGES),
          fetch(`${API_URLS.BLOGS}&category=ssl&limit=3`),
          fetch(API_URLS.FAQ)
        ]);
        const [tJson, pJson, bJson, fJson] = await Promise.all([
          tRes.json(),
          pRes.json(),
          bRes.json(),
          fRes.json()
        ]);
        if (mounted) {
          const tData = Array.isArray(tJson.data) && tJson.data.length > 0 ? [{ key: 'all', label: 'Tümü' }, ...tJson.data.map(x => ({ key: x.key, label: x.label }))] : fallbackTabs;
          setTabs(tData);
          const pkgs = Array.isArray(pJson.data) && pJson.data.length > 0 ? pJson.data.map(pkg => {
            // Parse features - obje yapısını koru
            let rawFeatures = [];
            try { 
              const j = JSON.parse(pkg.features); 
              rawFeatures = Array.isArray(j) ? j : []; 
            } catch { 
              rawFeatures = []; 
            }
            
            const parsedFeatures = rawFeatures.map(f => {
              if (typeof f === 'string') {
                return { name: f, text: f, included: true };
              }
              if (f && typeof f === 'object') {
                return {
                  name: f.name || f.text || '',
                  text: f.text || f.name || '',
                  included: f.included !== false
                };
              }
              return { name: String(f), text: String(f), included: true };
            });
            
            return {
              id: pkg.id,
              name: pkg.name,
              type: pkg.type,
              warranty: pkg.warranty,
              features: parsedFeatures,
              price: pkg.price,
              period: pkg.period,
              best: pkg.is_best === 1 || pkg.is_best === '1' || pkg.is_best === true
            };
          }) : seedCertificates;
          setCerts(pkgs);
          
          // Filter blogs by SSL category (handle both array or {blogs: [], data: []})
          const blogsArray = Array.isArray(bJson) ? bJson : (Array.isArray(bJson?.blogs) ? bJson.blogs : (Array.isArray(bJson?.data) ? bJson.data : []));
          if (Array.isArray(blogsArray)) {
            const sslBlogs = blogsArray.filter(blog => {
              const catRaw = (blog.category_name || blog.category || '').toString();
              const cat = catRaw.toLowerCase();
              return cat.includes('ssl');
            });
            setBlogs(sslBlogs.slice(0, 3));
          }
          
          // Filter FAQs by SSL category (map category_id -> category title)
          const categories = Array.isArray(fJson?.categories) ? fJson.categories : [];
          const questions = Array.isArray(fJson?.questions) ? fJson.questions : [];
          const idToTitle = categories.reduce((acc, c) => { acc[c.id] = (c.title || '').toString(); return acc; }, {});
          const sslFaqs = questions.filter(q => {
            const title = (idToTitle[q.category_id] || '').toLowerCase();
            return title.includes('ssl');
          });
          setFaqs(sslFaqs.slice(0, 3));
        }
      } catch (e) {
        // fallback stays
      }
    })();
    
    // Fetch settings for WhatsApp number
    (async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
        if (res.ok) {
          const json = await res.json();
          if (json.status === 'success' && json.data?.whatsapp) {
            setWhatsappNumber(json.data.whatsapp);
          }
        }
      } catch (e) {
        console.error('WhatsApp numarası alınamadı:', e);
      }
    })();
    
    return () => { mounted = false; };
  }, []);

  const onSelectTab = (key) => {
    setActive(key);
    const params = new URLSearchParams(location.search);
    params.set('tab', key);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: false });
  };

  const items = useMemo(() => {
    if (active === 'all') return certs;
    return certs.filter((c) => c.type === active);
  }, [active, certs]);

  // DB-driven section helpers
  const additional = useMemo(() => pageContent?.additional_sections || {}, [pageContent]);
  const hero = additional?.hero || {};
  const heroTitle = pageContent?.hero_title || 'SSL Sertifikası';
  const heroSubtitle = pageContent?.hero_badge_text || 'En Ucuz SSL Sertifikaları';
  const heroDesc = pageContent?.hero_description || 'SSL sertifikası ile Google arama sonuçlarında yükselin, web sitenizin güvenliğini garanti altına alın!';
  const heroImage = hero?.image_url || 'https://cdn.natrocdn.com/ResourceFiles/v1/images/ssl/v2/banner.png';
  const whySection = additional?.why_ssl_section || {};
  const whyCards = Array.isArray(whySection.cards) ? whySection.cards : [];
  const seoGainSection = additional?.seo_gain_section || {};
  const sslTypesSection = additional?.ssl_types_section || {};
  const sslTypeCards = Array.isArray(sslTypesSection.cards) ? sslTypesSection.cards : [];
  const whichSection = additional?.which_ssl_section || {};
  const secureSection = additional?.is_site_secure_section || {};
  const benefitsSection = additional?.ssl_benefits_section || {};
  const benefitCards = Array.isArray(benefitsSection.cards) ? benefitsSection.cards : [];
  const valueProductsSection = additional?.value_products_section || {};
  const valueProductCards = Array.isArray(valueProductsSection.cards) ? valueProductsSection.cards : [];

  const mapIcon = (name, fallbackIcon) => {
    switch (name) {
      case 'Shield': return Shield;
      case 'ShieldCheck': return ShieldCheck;
      case 'BadgeCheck': return BadgeCheck;
      case 'Lock': return Lock;
      case 'TrendingUp': return TrendingUp;
      case 'Globe': return Globe;
      case 'CheckCircle': return CheckCircle;
      default: return fallbackIcon || Check;
    }
  };

  return (
    <div className="bg-white">
      {/* Hero */}
      <section 
        className="relative overflow-hidden bg-gradient-to-r from-yellow-600 to-amber-600 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-16 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl font-extrabold mb-3 tracking-tight">{heroTitle}</h1>
              <h2 className="text-xl sm:text-2xl font-bold mb-4">{heroSubtitle}</h2>
              <p className="text-white/90 mb-6">{heroDesc}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 justify-center lg:justify-start">
                  <Check className="w-5 h-5 text-white" />
                  <span className="text-white/90">Google'da Yükselin</span>
                </div>
                <div className="flex items-center gap-2 justify-center lg:justify-start">
                  <Check className="w-5 h-5 text-white" />
                  <span className="text-white/90">Trafiğinizi Arttırın</span>
                </div>
                <div className="flex items-center gap-2 justify-center lg:justify-start">
                  <Check className="w-5 h-5 text-white" />
                  <span className="text-white/90">Online Ödeme Alın</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <MediaDisplay
                src={heroImage}
                alt="SSL Sertifikası"
                className="max-w-full h-auto"
                videoProps={{ controls: true }}
                imgProps={{ loading: "lazy" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section 
        className="border-b border-gray-100 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative flex flex-wrap gap-3 py-4">
            {tabs.map((cat) => (
              <button
                key={cat.key}
                onClick={() => onSelectTab(cat.key)}
                className={`relative px-4 h-10 rounded-full border text-sm font-medium transition-all ${active === cat.key ? 'bg-yellow-600 border-yellow-600 text-white shadow-[0_6px_20px_-8px_rgba(234,179,8,0.9)]' : 'bg-white border-gray-200 text-gray-700 hover:border-yellow-500 hover:text-yellow-600'}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {items.map((c, idx) => (
              <CertificateCard 
                key={c.id} 
                cert={c} 
                index={idx}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Neden SSL Sertifikası Almalıyım? */}
      <section 
        className="border-t border-gray-100 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-8">{whySection.title || 'Neden SSL Sertifikası Almalıyım?'}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {(whyCards.length ? whyCards : []).map((x, i) => {
                  const Icon = mapIcon(x.icon, Shield);
                  return (
                    <div key={i} className="border rounded-2xl p-6 bg-white hover:shadow-lg transition-all text-center lg:text-left">
                      <div className="w-11 h-11 rounded-xl bg-yellow-100 text-yellow-700 flex items-center justify-center mb-3 mx-auto lg:mx-0">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="font-semibold text-gray-900 mb-1">{x.title || ''}</div>
                      <div className="text-gray-600 text-sm">{x.description || x.desc || ''}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg overflow-hidden">
              <MediaDisplay
                src={whySection.image_url || "https://via.placeholder.com/600x500/10B981/FFFFFF?text=SSL+Sertifikası"}
                alt="Neden SSL Sertifikası Almalıyım?"
                className="w-full h-full object-cover"
                videoProps={{ controls: true }}
                imgProps={{ loading: "lazy" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SSL Sertifikası ile Google'da Yükselin! */}
      <section 
        className="py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1 text-center md:text-left">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{seoGainSection.title || "SSL Sertifikası ile Google'da Yükselin!"}</h3>
              <p className="text-gray-700">{seoGainSection.description || ''}</p>
            </div>
            <div className="order-1 md:order-2 text-center">
              <MediaDisplay
                className="mx-auto max-w-md rounded-xl border border-gray-100"
                alt="google-seo"
                src={seoGainSection.image_url || "https://cdn.natrocdn.com/ResourceFiles/v1/images/Server/xtream/g6-2.png"}
                videoProps={{ controls: true }}
                imgProps={{ loading: "lazy" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SSL Sertifikası Türleri */}
      <section 
        className="border-t border-gray-100 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-14">
          <h3 className="text-2xl font-extrabold text-gray-900 mb-8">{sslTypesSection.title || 'SSL Sertifikası Türleri'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(sslTypeCards.length ? sslTypeCards : []).map((x, idx) => (
              <div key={idx} className="border rounded-2xl p-6 bg-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="text-lg font-semibold text-gray-900">{x.title || ''}</div>
                </div>
                <div className="text-gray-600 text-sm">{x.description || x.desc || ''}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Web Sitem Güvende mi? */}
      <section 
        className="bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg overflow-hidden">
              <MediaDisplay
                src={secureSection.image_url || "https://via.placeholder.com/600x400/10B981/FFFFFF?text=Web+Güvenlik"}
                alt="Web Sitem Güvende mi?"
                className="w-full h-full object-cover"
                videoProps={{ controls: true }}
                imgProps={{ loading: "lazy" }}
              />
            </div>
            <div className="text-center lg:text-left">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{secureSection.title || 'Web Sitem Güvende mi?'}</h3>
              <p className="text-gray-600 mb-4">{secureSection.description || ''}</p>
              <ul className="space-y-2">
                {[
                  'Adres çubuğunda HTTPS görünüyor',
                  'Form ve ödeme sayfaları SSL ile korunuyor',
                  'Güncel SSL sürümleri ve güçlü şifre paketleri kullanılıyor'
                ].map((t,i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700 justify-center lg:justify-start"><Check className="w-5 h-5 text-yellow-600 mt-0.5" />{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SSL Sertifikası Neler Sağlar? (veri varsa) */}
      {benefitCards.length > 0 && (
        <section
          style={{ 
            opacity: contentReady ? 1 : 0, 
            transition: 'opacity 0.6s ease-in' 
          }}
        >
          <div className="max-w-7xl mx-auto px-6 py-14">
            <h3 className="text-2xl font-extrabold text-gray-900 mb-8">{benefitsSection.title || 'SSL Sertifikası Neler Sağlar?'}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefitCards.map((x, i) => {
                const Icon = mapIcon(x.icon, Lock);
                const text = x.title || x.text || '';
                return (
                  <div key={i} className="border rounded-2xl p-6 bg-white">
                    <div className="w-11 h-11 rounded-xl bg-yellow-100 text-yellow-700 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="font-semibold text-gray-900 mb-1">{text}</div>
                    <div className="text-sm text-gray-600">{x.description || x.desc || ''}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SSL Sertifikaları Karşılaştırması */}
      <section 
        className="bg-gray-50 border-t border-gray-100"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-14">
          <h3 className="text-2xl font-extrabold text-yellow-700 mb-6 text-center lg:text-left">SSL Sertifikaları Karşılaştırması</h3>
          
          {/* Desktop: Table View */}
          <div className="hidden md:block rounded-2xl overflow-hidden border border-gray-200 bg-white">
            <div className="bg-gradient-to-r from-yellow-600 to-amber-600 text-white text-sm font-semibold px-6 py-3 grid grid-cols-4">
              <div>Özellik</div>
              <div>DV</div>
              <div>OV</div>
              <div>EV</div>
            </div>
            {(() => {
              // Get packages by type for comparison
              const dvPkg = certs.find(p => p.type === 'dv');
              const ovPkg = certs.find(p => p.type === 'ov');
              const evPkg = certs.find(p => p.type === 'ev');
              
              const comparisonData = [
                { name: 'Doğrulama', dv: 'Domain', ov: 'Kurumsal', ev: 'Gelişmiş Kurumsal' },
                { name: 'Kurulum Süresi', dv: 'Dakikalar', ov: '1-3 Gün', ev: '1-5 Gün' },
                { name: 'Tarayıcı Uyumluluğu', dv: 'Yüksek', ov: 'Yüksek', ev: 'Yüksek' },
                { name: 'Güven Damgası', dv: true, ov: true, ev: true },
                { name: 'Yeşil Adres Çubuğu', dv: false, ov: false, ev: true },
                { name: 'Garanti', dv: dvPkg?.warranty || '$10,000', ov: ovPkg?.warranty || '$250,000', ev: evPkg?.warranty || '$1,000,000' },
                { name: 'Fiyat', dv: (dvPkg?.price || '15,99$') + ' /yıllık', ov: (ovPkg?.price || '89,99$') + ' /yıllık', ev: (evPkg?.price || '199,99$') + ' /yıllık' }
              ];
              
              return comparisonData.map((row, idx) => (
                <div key={idx} className={`px-6 py-3 grid grid-cols-4 items-center text-sm ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-t border-gray-100`}>
                  <div className="font-medium text-gray-900">{row.name}</div>
                  {[row.dv, row.ov, row.ev].map((val, i) => (
                    <div key={i} className="text-gray-800">
                      {typeof val === 'boolean' ? (
                        val ? <Check className="w-5 h-5 text-yellow-600" /> : <span className="text-gray-400">—</span>
                      ) : (
                        val
                      )}
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>

          {/* Mobile: Accordion View */}
          <div className="md:hidden space-y-3">
            {(() => {
              const dvPkg = certs.find(p => p.type === 'dv');
              const ovPkg = certs.find(p => p.type === 'ov');
              const evPkg = certs.find(p => p.type === 'ev');
              
              const sslTypes = [
                { 
                  id: 'dv', 
                  name: 'DV SSL', 
                  description: 'Domain Doğrulama',
                  pkg: dvPkg,
                  data: {
                    'Doğrulama': 'Domain',
                    'Kurulum Süresi': 'Dakikalar',
                    'Tarayıcı Uyumluluğu': 'Yüksek',
                    'Güven Damgası': true,
                    'Yeşil Adres Çubuğu': false,
                    'Garanti': dvPkg?.warranty || '$10,000',
                    'Fiyat': (dvPkg?.price || '15,99$') + ' /yıllık'
                  }
                },
                { 
                  id: 'ov', 
                  name: 'OV SSL', 
                  description: 'Kurumsal Doğrulama',
                  pkg: ovPkg,
                  data: {
                    'Doğrulama': 'Kurumsal',
                    'Kurulum Süresi': '1-3 Gün',
                    'Tarayıcı Uyumluluğu': 'Yüksek',
                    'Güven Damgası': true,
                    'Yeşil Adres Çubuğu': false,
                    'Garanti': ovPkg?.warranty || '$250,000',
                    'Fiyat': (ovPkg?.price || '89,99$') + ' /yıllık'
                  }
                },
                { 
                  id: 'ev', 
                  name: 'EV SSL', 
                  description: 'Gelişmiş Kurumsal Doğrulama',
                  pkg: evPkg,
                  data: {
                    'Doğrulama': 'Gelişmiş Kurumsal',
                    'Kurulum Süresi': '1-5 Gün',
                    'Tarayıcı Uyumluluğu': 'Yüksek',
                    'Güven Damgası': true,
                    'Yeşil Adres Çubuğu': true,
                    'Garanti': evPkg?.warranty || '$1,000,000',
                    'Fiyat': (evPkg?.price || '199,99$') + ' /yıllık'
                  }
                }
              ];

              return sslTypes.map((sslType) => {
                const categoryKey = sslType.id;
                const isExpanded = expandedCategories[categoryKey];
                const features = Object.entries(sslType.data);
                const visibleFeatures = isExpanded ? features : features.slice(0, 3);

                return (
                  <Accordion key={sslType.id} type="single" collapsible className="border border-yellow-200 rounded-lg bg-white">
                    <AccordionItem value={sslType.id} className="border-0">
                      <AccordionTrigger className="px-3 py-2.5 hover:no-underline">
                        <div className="flex items-center justify-between w-full pr-3">
                          <div className="text-left flex-1">
                            <div className="font-bold text-gray-900 text-sm">{sslType.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{sslType.description}</div>
                          </div>
                          <div className="text-right ml-3 flex-shrink-0">
                            <div className="font-bold text-yellow-600 text-sm">{sslType.data['Fiyat']}</div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-3">
                        <div className="space-y-2">
                          {visibleFeatures.map(([key, value], idx) => (
                            <div key={idx} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                              <span className="text-xs font-medium text-gray-700">{key}:</span>
                              <span className="text-xs text-gray-900">
                                {typeof value === 'boolean' ? (
                                  value ? <Check className="w-4 h-4 text-yellow-600 inline" /> : <span className="text-gray-400">—</span>
                                ) : (
                                  value
                                )}
                              </span>
                            </div>
                          ))}
                          {features.length > 3 && (
                            <button
                              onClick={() => setExpandedCategories(prev => ({
                                ...prev,
                                [categoryKey]: !prev[categoryKey]
                              }))}
                              className="w-full mt-2 text-xs font-semibold text-yellow-600 hover:text-yellow-700 py-1.5"
                            >
                              {isExpanded ? 'Daha az göster' : 'Daha fazla göster'}
                            </button>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* SSL Sertifikası ile İlgili Güncel İçerikler */}
      <section
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-14">
          <h3 className="text-2xl font-extrabold text-gray-900 mb-6">SSL Sertifikası ile İlgili Güncel İçerikler</h3>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {blogs.length > 0 ? blogs.map((blog, i) => {
              const hasValidImage = blog.image && !blog.image.includes('placeholder');
              const excerpt = blog.excerpt || (() => {
                const cleanContent = blog.content?.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ').trim() || '';
                return cleanContent.length > 100 ? cleanContent.substring(0, 100) + '...' : cleanContent;
              })();
              
              return (
                <Card key={blog.id || i} className="overflow-hidden border-yellow-100 hover:shadow-lg transition-shadow">
                  {hasValidImage ? (
                    <div className="aspect-[16/9] bg-yellow-50 overflow-hidden">
                      <MediaDisplay
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        videoProps={{ controls: true }}
                        imgProps={{
                          loading: "lazy",
                          onError: (e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-yellow-500 to-orange-700 flex items-center justify-center"><svg class="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-yellow-500 to-orange-700 flex items-center justify-center">
                      <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                  )}
                  <CardContent className="p-5 space-y-3">
                    <div className="inline-flex items-center px-3 py-1 bg-yellow-50 text-yellow-600 text-xs font-semibold rounded-full">
                      <BookOpen className="w-3.5 h-3.5 mr-1" />
                      {blog.category_name || blog.category || 'SSL'}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{blog.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{excerpt}</p>
                    <button
                      onClick={() => window.location.href = `/blog/${blog.slug || blog.id}`}
                      className="inline-flex items-center text-sm font-semibold text-yellow-600 hover:text-yellow-700"
                    >
                      Yazıyı Oku
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </CardContent>
                </Card>
              );
            }) : (
              <div className="col-span-full text-center py-8 text-gray-500">
                SSL kategorisinde blog yazısı bulunamadı.
              </div>
            )}
          </div>
        </div>
      </section>


      {/* Sıkça sorulan sorular */}
      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Sıkça Sorulan <span className="text-yellow-600">Sorular</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              SSL Sertifikası ile ilgili merak ettiğiniz soruların cevaplarını burada bulabilirsiniz
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            {faqs.length > 0 ? (
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => (
                  <AccordionItem key={faq.id || i} value={`item-${i}`}>
                    <AccordionTrigger className="text-left">
                      <div className="flex items-center">
                        <HelpCircle className="w-5 h-5 mr-3 text-yellow-600" />
                        {faq.question}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">SSL kategorisinde soru bulunamadı.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* SSL Contact Section */}
      <section 
        className="py-20 bg-gradient-to-br from-yellow-500 via-yellow-600 to-amber-600 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">SSL Sertifikası İhtiyacınız İçin Bize Ulaşın</h2>
            <p className="text-xl text-yellow-100 mb-8 max-w-3xl mx-auto">
              Web sitenizin güvenliği için SSL sertifikası çözümlerimiz hakkında detaylı bilgi almak ve size özel öneriler için ekibimizle iletişime geçin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/iletisim">
                <button className="inline-flex items-center justify-center bg-white text-yellow-600 hover:bg-gray-100 px-8 py-4 text-lg rounded-lg font-semibold">
                  <Mail className="w-5 h-5 mr-2" />
                  İletişime Geç
                </button>
              </Link>
              <a
                href={`https://api.whatsapp.com/send/?phone=${encodeURIComponent(whatsappNumber)}&text=${encodeURIComponent('Merhaba, SSL sertifikası hizmetleriniz hakkında bilgi almak istiyorum.')}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="inline-flex items-center justify-center bg-emerald-700 hover:bg-emerald-800 px-8 py-4 text-lg rounded-lg font-semibold border-2 border-white">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp İletişim
                </button>
              </a>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Clock className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">7/24 Destek</h3>
                <p className="text-yellow-100">SSL sertifikası konusunda kesintisiz destek</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <ShieldCheck className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Güvenli Web</h3>
                <p className="text-yellow-100">Dakikalar içinde SSL kurulumu</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Award className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Uzman Ekip</h3>
                <p className="text-yellow-100">SSL sertifikası konusunda deneyimli ekibimiz</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SSLCertificatesPage;


